import { ThemeIconPack } from "@instacover/helpers"
import { unreachableWithFallback } from "@instacover/utils"
import { useParams } from "@tanstack/react-router"

import { useSessionDetail } from "@/services/dexie"

import { useTheme } from "../../styles/themes/themes"
import { preloadImages } from "../../utils/browser"
import { lazyWithRetry } from "../../utils/react"
import { VehicleType } from "../../utils/types"
import { directImageUrls } from "./InstructionImageDirect"
import { InstructionImageProps } from "./InstructionImageType"

const InstructionImageDirect = lazyWithRetry(() => import("./InstructionImageDirect"), "InstructionImageDirect")

const InstructionImageDefault3D = lazyWithRetry(
  () => import("./InstructionImageDefault3D"),
  "InstructionImageDefault3D",
)

const InstructionImageKooperativa = lazyWithRetry(
  () => import("./InstructionImageKooperativa"),
  "InstructionImageKooperativa",
)

const InstructionImageBikeDefault = lazyWithRetry(
  () => import("./InstructionImageBikeDefault"),
  "InstructionImageBikeDefault",
)

type PreloadIconsAndAnimationParams = {
  iconPack: ReturnType<typeof useTheme>["activeTheme"]["iconPack"]
  iconPackMotorcycle: ReturnType<typeof useTheme>["activeTheme"]["iconPackMotorcycle"]
  activeIconPack: VehicleType
}

export async function preloadIconsAndAnimation(params: PreloadIconsAndAnimationParams) {
  const { iconPack, iconPackMotorcycle, activeIconPack } = params

  // Motorcycle icon packs
  if (activeIconPack === "Motorcycle") {
    switch (iconPackMotorcycle) {
      case "motorcycleDefault":
        return InstructionImageBikeDefault.preload?.()
      default:
        return InstructionImageBikeDefault.preload?.()
    }
  }

  // PersonalVehicle icon packs
  switch (iconPack) {
    case ThemeIconPack.Kooperativa:
      return InstructionImageKooperativa.preload?.()
    case ThemeIconPack.Direct:
      await preloadImages(directImageUrls)
      return InstructionImageDirect.preload?.()
    case ThemeIconPack.Default3D:
    case undefined:
      return InstructionImageDefault3D.preload?.()
    default:
      return unreachableWithFallback(iconPack, InstructionImageDefault3D.preload?.())
  }
}

export function InstructionImage(props: InstructionImageProps) {
  const { sessionId = "" } = useParams({ strict: false })
  const { data: session } = useSessionDetail(sessionId)
  const {
    activeTheme: { iconPack },
  } = useTheme()

  if (session?.vehicleType === "Motorcycle") {
    return <InstructionImageBikeDefault {...props} />
  }

  switch (iconPack) {
    case ThemeIconPack.Kooperativa:
      return <InstructionImageKooperativa {...props} />
    case ThemeIconPack.Direct:
      return <InstructionImageDirect {...props} />
    case ThemeIconPack.Default3D:
    case undefined:
      return <InstructionImageDefault3D {...props} />
    default:
      return unreachableWithFallback(iconPack, <InstructionImageDefault3D {...props} />)
  }
}
